<template>
  <div v-bind='$attrs'>
    <label
      v-if='label'
      :for='name'
      class='block text-sm'
      :class='[(disabled && editMode) ? "text-gray-400" : "text-gray-700"]'
      data-cy='label'
    >
      <QuestionMarkCircleIcon
        v-if='help'
        class='question-mark-circle-icon mb-1 mr-0.5 inline-block h-4 w-4 cursor-pointer'
        @click='setHelpModalVisibility(true)'
      />
      <span
        :title='label'
        v-html='getLabel()'
      />
    </label>
    <div v-if='editMode'>
      <div class='group relative mt-1'>
        <DatePicker
          v-slot='{ inputValue, inputEvents, togglePopover, hidePopover }'
          v-model='proxyValue'
          :masks='masks'
          is-range
          mode='date'
          :attributes='[
            {
              key: "today",
              dates: new Date(),
              highlight: true,
              customData: { isToday: true },
            }
          ]'
          is24hr
          :timezone='timezone'
          :disabled='disabled'
          :popover='{ visibility: disabled ? "hidden" : "click" }'
          @keyup.delete='clearValue'
        >
          <input
            :id='name'
            :value='getValue(inputValue)'
            :name='name'
            type='text'
            :placeholder='placeholder'
            :aria-describedby='ariaDescribedBy'
            :disabled='disabled'
            class='block rounded-md text-sm shadow-sm'
            :class='[
              error ? "border-red-300 focus:ring-red-500 focus:border-red-500 caret-red-500 selection:bg-red-300 selection:text-red-900" : "border-gray-300 focus:ring-gray-500 focus:border-gray-500",
              disabled ? "bg-gray-50 text-gray-400" : "",
              isFilterField ? "h-[1.6rem]" : "",
              (isFilterField && getValue(inputValue)) ? "min-w-full" : "w-full",
              inputClasses
            ]'
            data-cy='input'
            v-on='inputEvents'
            @focusin='togglePopover'
            @focusout='hidePopover'
            @keyup.delete='clearValue'
          >
          <div
            v-if='!isFilterField'
            class='pointer-events-none absolute inset-y-0 right-3 flex items-center py-3 text-gray-400'
          >
            <CalendarIcon
              class='h-6 w-6 shrink-0'
              aria-hidden='true'
            />
          </div>
          <div
            v-else
            class='absolute inset-y-0 right-3 z-10 flex h-0 w-0 cursor-pointer items-center opacity-0 transition-opacity group-hover:h-auto group-hover:w-auto group-hover:opacity-100'
            title='Filter empty values'
            @click='filterEmptyValues'
          >
            <FilterIcon
              class='h-4 w-4 shrink-0 opacity-0 group-hover:opacity-100'
              aria-hidden='true'
            />
          </div>
        </DatePicker>
      </div>
      <InputError :error='error'/>
    </div>
    <div v-else>
      <div
        class='text-sm'
        data-cy='preview-value'
      >
        {{ proxyValueString }}
      </div>
    </div>
  </div>
  <Modal
    :open='helpVisible'
    :title='label'
    @close-modal='setHelpModalVisibility(false)'
  >
    <template #content>
      <p v-html='help'/>
    </template>
  </Modal>

</template>

<script lang="ts">
  import { defineComponent, ref, watch, onMounted, computed } from 'vue'
  import useInputFieldBase, { InputFieldProps, InputFieldComponents, InputFieldEmits } from '../../../compositions/useInputFieldBase'
  import useInputFieldHasHelp, { InputFieldHasHelpProps, InputFieldHasHelpComponents } from '../../../compositions/useInputFieldHasHelp'
  import { DatePicker } from 'v-calendar'
  import { CalendarIcon, FilterIcon } from '@heroicons/vue/outline'
  import 'v-calendar/dist/style.css'
  import { formatUtcDate } from '@/utils/helpers'

  export default defineComponent({
    name: 'DateRangeField',
    components: {
      FilterIcon,
      ...InputFieldComponents,
      ...InputFieldHasHelpComponents,
      DatePicker,
      CalendarIcon,
    },
    props: {
      ...InputFieldProps,
      ...InputFieldHasHelpProps,
      timezone: {
        type: String,
        default: 'UTC',
      },
    },
    emits: [
      ...InputFieldEmits,
    ],
    setup(props) {
      const { helpVisible, setHelpModalVisibility } = useInputFieldHasHelp()
      const { updateValue, clearValue, getLabel } = useInputFieldBase()

      const masks = {
        input: ['DD/MM/YYYY'],
      }
      const proxyValue = ref()

      const proxyValueString = computed(() => {
        if (proxyValue.value === '__EMPTY__') return 'Empty'
        else return `${formatUtcDate(proxyValue.value?.start).replaceAll('/', '.')} ↔ ${formatUtcDate(proxyValue.value?.end).replaceAll('/', '.')}`
      })

      watch(proxyValue, (value) => {
        if (value.start instanceof Date) {
          value.start.setUTCHours(0, 0, 0, 0)
        } else if (typeof value.start === 'string' || value.start instanceof String) {
          value.start = new Date(value.start)
          value.start.setUTCHours(0, 0, 0, 0)
        }

        if (value.end instanceof Date) {
          value.end.setUTCHours(23, 59, 59, 999)
        } else if (typeof value.end === 'string' || value.end instanceof String) {
          value.end = new Date(value.end)
          value.end.setUTCHours(23, 59, 59, 999)
        }

        updateValue(value)
      })

      watch(() => props.modelValue, (value) => {
        proxyValue.value = value
      })

      onMounted(() => {
        if(props.modelValue) {
          if(typeof props.modelValue === 'string' || props.modelValue instanceof String) {
            const dateParts = props.modelValue.split(';')

            proxyValue.value = {
              start: new Date(dateParts[0]),
              end: new Date(dateParts[1]),
            }
          } else {
            proxyValue.value = props.modelValue
          }
        }
      })

      const filterEmptyValues = () => {
        if (props.modelValue === '__EMPTY__') updateValue('')
        else updateValue('__EMPTY__')
      }

      const getValue = (inputValue) => {

        if (props.modelValue === '__EMPTY__') {
          return 'Empty'
        }

        if ((!inputValue.end || !inputValue.start) && typeof proxyValue.value === 'string') {
          return proxyValue.value.replaceAll('-', ' ↔ ')
        }

        return !inputValue.start && !inputValue.end ? '' : inputValue.start.replaceAll('/', '.') + ' ↔ ' + inputValue.end.replaceAll('/', '.')
      }

      return {
        updateValue, clearValue, getLabel, helpVisible, setHelpModalVisibility, masks, proxyValue, proxyValueString, filterEmptyValues, getValue,
      }
    },
  })
</script>
